/**
 * useCredits Hook
 * Custom hook for managing credit-related state and operations
 */

import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import {
  CreditsService,
  type CreditBalance,
  type CreditAnalytics,
  type CreditTransaction,
  type UsageLog
} from '@/services/credits-service'
import { useAuth } from '@/store/slices/auth-slice'
import { useWorkspace } from './use-workspace'

/**
 * Detect HTTP authentication errors (no token / 401) thrown by api-client.
 * Used to suppress toasts during the logout transition — once the access
 * token has been cleared, in-flight or follow-up credit fetches throw
 * `HttpError{ type: 'authentication' }`, and the user is already being
 * redirected to /signin so a toast is just noise.
 */
function isAuthError(err: unknown): boolean {
  const e = err as { type?: string; status?: number } | undefined
  return e?.type === 'authentication' || e?.status === 401
}

/**
 * Credit analytics filters interface
 */
export interface CreditAnalyticsFilters {
  startDate?: string
  endDate?: string
  userId?: string
  provider?: string
  limit?: number
  offset?: number
}

/**
 * Credit transaction filters interface
 */
export interface CreditTransactionFilters {
  transactionType?: 'CREDIT' | 'DEBIT'
  startDate?: string
  endDate?: string
  limit?: number
  offset?: number
}

/**
 * Usage logs filters interface
 */
export interface UsageLogsFilters {
  userId?: string
  startDate?: string
  endDate?: string
  limit?: number
  offset?: number
}

/**
 * Credit balance hook return interface.
 * Includes optional per-user cap info so the sidebar can show
 * user-specific remaining credits when a monthly cap is set.
 */
export interface UseCreditBalanceReturn {
  balance: CreditBalance | null
  /** Per-user cap info — present when the current user has a monthly cap */
  userCap: { remainingPcu: number; capPcu: number; usagePcu: number } | null
  isLoading: boolean
  error: string | null
  fetchBalance: () => Promise<void>
  refresh: () => Promise<void>
  clearError: () => void
}

/**
 * Credit analytics hook return interface
 */
export interface UseCreditAnalyticsReturn {
  analytics: CreditAnalytics | null
  isLoading: boolean
  error: string | null
  fetchAnalytics: (filters?: CreditAnalyticsFilters) => Promise<void>
  refresh: () => Promise<void>
  clearError: () => void
}

/**
 * Credit transactions hook return interface
 */
export interface UseCreditTransactionsReturn {
  transactions: CreditTransaction[]
  isLoading: boolean
  error: string | null
  fetchTransactions: (filters?: CreditTransactionFilters) => Promise<void>
  refresh: () => Promise<void>
  clearError: () => void
}

/**
 * Usage logs hook return interface
 */
export interface UseUsageLogsReturn {
  usageLogs: UsageLog[]
  isLoading: boolean
  error: string | null
  fetchUsageLogs: (filters?: UsageLogsFilters) => Promise<void>
  refresh: () => Promise<void>
  clearError: () => void
}

/**
 * Credit management hook return interface
 */
export interface UseCreditManagementReturn {
  isPurchasing: boolean
  isSettingCap: boolean
  isResettingUsage: boolean
  error: string | null
  purchaseCredits: (amountPcu: string, description: string) => Promise<void>
  setUserCap: (userId: string, monthlyCapPcu: string) => Promise<void>
  resetUserUsage: (userId: string) => Promise<void>
  clearError: () => void
}

/**
 * Comprehensive credits hook return interface
 */
export interface UseCreditsReturn {
  // Data
  balance: CreditBalance | null
  analytics: CreditAnalytics | null
  transactions: CreditTransaction[]
  usageLogs: UsageLog[]
  
  // Loading states
  isLoading: boolean
  isRefreshing: boolean
  
  // Error states
  error: string | null
  
  // Filter state
  selectedTimeRange: '7d' | '30d' | '90d' | 'all'
  setSelectedTimeRange: (range: '7d' | '30d' | '90d' | 'all') => void
  selectedUser: string | undefined
  setSelectedUser: (userId: string | undefined) => void
  selectedProvider: string | undefined
  setSelectedProvider: (provider: string | undefined) => void
  
  // Actions
  fetchAll: (filters?: CreditAnalyticsFilters) => Promise<void>
  refresh: () => Promise<void>
  clearError: () => void
  
  // Management actions
  purchaseCredits: (amountPcu: string, description: string) => Promise<void>
  setUserCap: (userId: string, monthlyCapPcu: string) => Promise<void>
  resetUserUsage: (userId: string) => Promise<void>
  
  // Management states
  isPurchasing: boolean
  isSettingCap: boolean
  isResettingUsage: boolean
}

/**
 * Hook for managing workspace credit balance.
 * Also fetches per-user cap info so the sidebar can show the user's
 * remaining credits when a monthly usage cap is configured.
 */
export function useCreditBalance(): UseCreditBalanceReturn {
  const { workspace } = useWorkspace()
  const { user } = useAuth()
  const [balance, setBalance] = useState<CreditBalance | null>(null)
  const [userCap, setUserCap] = useState<UseCreditBalanceReturn['userCap']>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * Fetch credit balance and user cap from server
   */
  const fetchBalance = useCallback(async () => {
    if (!workspace?.id) {
      setError('No workspace ID available')
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const balanceData = await CreditsService.getCreditBalance(workspace.id)
      setBalance(balanceData)

      /** Fetch user cap if authenticated — runs in parallel conceptually but after balance */
      if (user?.id) {
        try {
          const settings = await CreditsService.getUserSettings(workspace.id, user.id)
          if (settings?.monthlyUsageCapPcu) {
            const capPcu = parseFloat(settings.monthlyUsageCapPcu)
            const usagePcu = parseFloat(settings.monthlyUsagePcu || '0')
            if (capPcu > 0) {
              setUserCap({
                remainingPcu: Math.max(0, capPcu - usagePcu),
                capPcu,
                usagePcu,
              })
            } else {
              setUserCap(null)
            }
          } else {
            setUserCap(null)
          }
        } catch {
          /** User cap fetch is best-effort — don't fail the whole balance load */
          setUserCap(null)
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch credit balance'
      setError(errorMessage)
      // Suppress toast for auth errors — these fire during logout while
      // mounted credit consumers race against the token clear, and the
      // user is already being redirected to /signin.
      if (!isAuthError(err)) {
        toast.error('Failed to load credit balance', {
          description: errorMessage
        })
      }
    } finally {
      setIsLoading(false)
    }
  }, [workspace?.id, user?.id])

  /**
   * Refresh credit balance
   */
  const refresh = useCallback(async () => {
    await fetchBalance()
  }, [fetchBalance])

  // Auto-fetch on workspace change
  useEffect(() => {
    if (workspace?.id) {
      fetchBalance()
    }
  }, [workspace?.id, fetchBalance])

  return {
    balance,
    userCap,
    isLoading,
    error,
    fetchBalance,
    refresh,
    clearError
  }
}

/**
 * Hook for credit analytics
 */
export function useCreditAnalytics(): UseCreditAnalyticsReturn {
  const { workspace } = useWorkspace()
  const [analytics, setAnalytics] = useState<CreditAnalytics | null>(null)
  const [isLoading, setIsLoading] = useState(true) // Start with loading state
  const [error, setError] = useState<string | null>(null)

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * Fetch credit analytics from server
   */
  const fetchAnalytics = useCallback(async (filters?: CreditAnalyticsFilters) => {
    if (!workspace?.id) {
      setError('No workspace ID available')
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      
      const [
        workspaceSummary,
        usageByUser,
        usageByModel,
        usageByProvider,
        transactionsData,
        usageLogsData,
        balanceData,
      ] = await Promise.all([
        CreditsService.getWorkspaceSummary(workspace.id),
        CreditsService.getUsageByUser(workspace.id, {
          startDate: filters?.startDate,
          endDate: filters?.endDate,
          limit: filters?.limit || 50,
          offset: filters?.offset,
        }),
        CreditsService.getUsageByModel(workspace.id, {
          startDate: filters?.startDate,
          endDate: filters?.endDate,
          limit: 20,
        }),
        CreditsService.getUsageByProvider(workspace.id, {
          startDate: filters?.startDate,
          endDate: filters?.endDate,
          limit: 10,
        }),
        CreditsService.getTransactions(workspace.id, {
          startDate: filters?.startDate,
          endDate: filters?.endDate,
          limit: 50,
        }),
        CreditsService.getUsageLogs(workspace.id, {
          userId: filters?.userId,
          startDate: filters?.startDate,
          endDate: filters?.endDate,
          limit: 100,
        }),
        CreditsService.getCreditBalance(workspace.id),
      ])

      // Compute purchased credits from CREDIT transactions and hydrate balance fields
      const totalCreditsPurchased = (transactionsData?.transactions || [])
        .filter(t => t.transactionType === 'CREDIT')
        .reduce((sum, t) => sum + (parseFloat(t.amountPcu || '0') || 0), 0)
        .toString()

      // Enhance summary with accurate values for UI
      const summaryEnhanced = {
        ...workspaceSummary,
        totalTransactions: (transactionsData?.pagination?.total ?? transactionsData?.transactions?.length ?? 0),
        totalCreditsPurchased,
        currentBalance: balanceData?.creditBalancePcu || workspaceSummary.currentBalance,
        billingEnabled: !!balanceData?.billingEnabled,
      }

      const analyticsData: CreditAnalytics = {
        workspaceSummary: summaryEnhanced,
        usageByUser,
        usageByModel,
        usageByProvider,
        transactions: transactionsData.transactions,
        usageLogs: usageLogsData.usageLogs,
      }

      setAnalytics(analyticsData)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch credit analytics'
      setError(errorMessage)
      if (!isAuthError(err)) {
        toast.error('Failed to load credit analytics', {
          description: errorMessage
        })
      }
    } finally {
      setIsLoading(false)
    }
  }, [workspace?.id])

  /**
   * Refresh credit analytics
   */
  const refresh = useCallback(async () => {
    await fetchAnalytics()
  }, [fetchAnalytics])

  // Auto-fetch on workspace change
  useEffect(() => {
    if (workspace?.id) {
      fetchAnalytics()
    }
  }, [workspace?.id, fetchAnalytics])

  return {
    analytics,
    isLoading,
    error,
    fetchAnalytics,
    refresh,
    clearError
  }
}

/**
 * Hook for credit transactions
 */
export function useCreditTransactions(): UseCreditTransactionsReturn {
  const { workspace } = useWorkspace()
  const [transactions, setTransactions] = useState<CreditTransaction[]>([])
  const [isLoading, setIsLoading] = useState(true) // Start with loading state
  const [error, setError] = useState<string | null>(null)

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * Fetch credit transactions from server
   */
  const fetchTransactions = useCallback(async (filters?: CreditTransactionFilters) => {
    if (!workspace?.id) {
      setError('No workspace ID available')
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      
      const transactionsData = await CreditsService.getTransactions(workspace.id, filters)
      setTransactions(transactionsData.transactions)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch credit transactions'
      setError(errorMessage)
      if (!isAuthError(err)) {
        toast.error('Failed to load credit transactions', {
          description: errorMessage
        })
      }
    } finally {
      setIsLoading(false)
    }
  }, [workspace?.id])

  /**
   * Refresh credit transactions
   */
  const refresh = useCallback(async () => {
    await fetchTransactions()
  }, [fetchTransactions])

  // Auto-fetch on workspace change
  useEffect(() => {
    if (workspace?.id) {
      fetchTransactions()
    }
  }, [workspace?.id, fetchTransactions])

  return {
    transactions,
    isLoading,
    error,
    fetchTransactions,
    refresh,
    clearError
  }
}

/**
 * Hook for usage logs
 */
export function useUsageLogs(): UseUsageLogsReturn {
  const { workspace } = useWorkspace()
  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([])
  const [isLoading, setIsLoading] = useState(true) // Start with loading state
  const [error, setError] = useState<string | null>(null)

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * Fetch usage logs from server
   */
  const fetchUsageLogs = useCallback(async (filters?: UsageLogsFilters) => {
    if (!workspace?.id) {
      setError('No workspace ID available')
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      
      const usageLogsData = await CreditsService.getUsageLogs(workspace.id, filters)
      setUsageLogs(usageLogsData.usageLogs)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch usage logs'
      setError(errorMessage)
      if (!isAuthError(err)) {
        toast.error('Failed to load usage logs', {
          description: errorMessage
        })
      }
    } finally {
      setIsLoading(false)
    }
  }, [workspace?.id])

  /**
   * Refresh usage logs
   */
  const refresh = useCallback(async () => {
    await fetchUsageLogs()
  }, [fetchUsageLogs])

  // Auto-fetch on workspace change
  useEffect(() => {
    if (workspace?.id) {
      fetchUsageLogs()
    }
  }, [workspace?.id, fetchUsageLogs])

  return {
    usageLogs,
    isLoading,
    error,
    fetchUsageLogs,
    refresh,
    clearError
  }
}

/**
 * Hook for user credit settings
 */

/**
 * Hook for credit management operations (admin actions)
 */
export function useCreditManagement(): UseCreditManagementReturn {
  const { workspace } = useWorkspace()
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [isSettingCap, setIsSettingCap] = useState(false)
  const [isResettingUsage, setIsResettingUsage] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * Purchase credits for workspace
   */
  const purchaseCredits = useCallback(async (amountPcu: string, description: string) => {
    if (!workspace?.id) {
      setError('No workspace ID available')
      return
    }

    try {
      setIsPurchasing(true)
      setError(null)

      await CreditsService.purchaseCredits(workspace.id, amountPcu, description)
      toast.success(`Successfully purchased ${amountPcu} PCU credits`)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to purchase credits'
      setError(errorMessage)
      toast.error('Failed to purchase credits', {
        description: errorMessage
      })
      throw err
    } finally {
      setIsPurchasing(false)
    }
  }, [workspace?.id])

  /**
   * Set user credit cap
   */
  const setUserCap = useCallback(async (userId: string, monthlyCapPcu: string) => {
    if (!workspace?.id) {
      setError('No workspace ID available')
      return
    }

    try {
      setIsSettingCap(true)
      setError(null)
      
      await CreditsService.setUserCap(workspace.id, userId, monthlyCapPcu)
      toast.success('User credit cap updated successfully')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user credit cap'
      setError(errorMessage)
      toast.error('Failed to update user credit cap', {
        description: errorMessage
      })
      throw err
    } finally {
      setIsSettingCap(false)
    }
  }, [workspace?.id])

  /**
   * Reset user monthly usage
   */
  const resetUserUsage = useCallback(async (userId: string) => {
    if (!workspace?.id) {
      setError('No workspace ID available')
      return
    }

    try {
      setIsResettingUsage(true)
      setError(null)
      
      await CreditsService.resetUserUsage(workspace.id, userId)
      toast.success('User monthly usage reset successfully')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset user usage'
      setError(errorMessage)
      toast.error('Failed to reset user usage', {
        description: errorMessage
      })
      throw err
    } finally {
      setIsResettingUsage(false)
    }
  }, [workspace?.id])

  return {
    isPurchasing,
    isSettingCap,
    isResettingUsage,
    error,
    purchaseCredits,
    setUserCap,
    resetUserUsage,
    clearError
  }
}

/**
 * Comprehensive hook for credit management
 * Combines all credit-related operations in a single hook
 */
export function useCredits(): UseCreditsReturn {
  const [selectedTimeRange, setSelectedTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d')
  const [selectedUser, setSelectedUser] = useState<string>()
  const [selectedProvider, setSelectedProvider] = useState<string>()
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Individual hooks
  const balance = useCreditBalance()
  const analytics = useCreditAnalytics()
  const transactions = useCreditTransactions()
  const usageLogs = useUsageLogs()
  const management = useCreditManagement()

  // Calculate date range based on selection
  const getDateRange = useCallback(() => {
    const now = new Date()
    const ranges = {
      '7d': new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
      '30d': new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
      '90d': new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000),
      'all': undefined,
    }
    
    const startDate = ranges[selectedTimeRange]
    return {
      startDate: startDate?.toISOString(),
      endDate: now.toISOString(),
    }
  }, [selectedTimeRange])

  /**
   * Fetch all credit data with filters
   */
  const fetchAll = useCallback(async (filters?: CreditAnalyticsFilters) => {
    setIsRefreshing(true)
    
    const dateRange = getDateRange()
    const effectiveFilters = {
      ...filters,
      ...dateRange,
      userId: selectedUser || filters?.userId,
      provider: selectedProvider || filters?.provider,
    }

    try {
      await Promise.all([
        balance.fetchBalance(),
        analytics.fetchAnalytics(effectiveFilters),
        transactions.fetchTransactions({
          startDate: effectiveFilters.startDate,
          endDate: effectiveFilters.endDate,
          limit: 100,
        }),
        usageLogs.fetchUsageLogs({
          userId: effectiveFilters.userId,
          startDate: effectiveFilters.startDate,
          endDate: effectiveFilters.endDate,
          limit: 200,
        })
      ])
    } finally {
      setIsRefreshing(false)
    }
  }, [selectedTimeRange, selectedUser, selectedProvider, balance, analytics, transactions, usageLogs, getDateRange])

  /**
   * Refresh all data
   */
  const refresh = useCallback(async () => {
    await fetchAll()
  }, [fetchAll])

  /**
   * Clear all errors
   */
  const clearError = useCallback(() => {
    balance.clearError()
    analytics.clearError()
    transactions.clearError()
    usageLogs.clearError()
    management.clearError()
  }, [balance, analytics, transactions, usageLogs, management])

  /**
   * Purchase credits with automatic refresh
   */
  const purchaseCreditsWithRefresh = useCallback(async (amountPcu: string, description: string) => {
    await management.purchaseCredits(amountPcu, description)
    // Refresh all data after successful purchase
    await refresh()
  }, [management.purchaseCredits, refresh])

  // Auto-fetch on filter changes (individual hooks handle initial load)
  useEffect(() => {
    // Only refetch when filters change and we have data already loaded
    if (balance.balance || analytics.analytics) {
      fetchAll()
    }
  }, [selectedTimeRange, selectedUser, selectedProvider])

  return {
    // Data
    balance: balance.balance,
    analytics: analytics.analytics,
    transactions: transactions.transactions,
    usageLogs: usageLogs.usageLogs,
    
    // Loading states
    isLoading: balance.isLoading || analytics.isLoading || transactions.isLoading || usageLogs.isLoading,
    isRefreshing,
    
    // Error states
    error: balance.error || analytics.error || transactions.error || usageLogs.error || management.error,
    
    // Filter state
    selectedTimeRange,
    setSelectedTimeRange,
    selectedUser,
    setSelectedUser,
    selectedProvider,
    setSelectedProvider,
    
    // Actions
    fetchAll,
    refresh,
    clearError,
    
    // Management actions
    purchaseCredits: purchaseCreditsWithRefresh,
    setUserCap: management.setUserCap,
    resetUserUsage: management.resetUserUsage,
    
    // Management states
    isPurchasing: management.isPurchasing,
    isSettingCap: management.isSettingCap,
    isResettingUsage: management.isResettingUsage,
  }
}